import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { browserTz } from "@/lib/format";
import { Field } from "@/components/common";

let tzCache = null;

export const HOME_TYPES = [["house", "House"], ["condo", "Condo/Townhouse"], ["apartment", "Apartment"]];

export function TimezoneSelect({ value, onChange, testid = "timezone-select", id }) {
  const [zones, setZones] = useState(tzCache || []);
  useEffect(() => {
    if (tzCache) return;
    api.get("/timezones").then(({ data }) => {
      tzCache = data;
      setZones(data);
    });
  }, []);
  const list = zones.includes(value) || !value ? zones : [value, ...zones];
  return (
    <select id={id} className="q-input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      {list.length === 0 && <option value={value}>{value}</option>}
      {list.map((z) => (
        <option key={z} value={z}>{z.replace(/_/g, " ")}</option>
      ))}
    </select>
  );
}

export function CreateHubForm({ onCreated }) {
  const [name, setName] = useState("");
  const [tz, setTz] = useState(browserTz());
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [hh, setHh] = useState({ kids: true, pets: false, cars: false });
  const [tenure, setTenure] = useState(null);
  const [kind, setKind] = useState("family");
  const [homeType, setHomeType] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return setError("Give your hub a name");
    setBusy(true);
    try {
      const { data } = await api.post("/organizations", kind === "roommates" ? { name, timezone: tz, type: "roommates" } : { name, timezone: tz, household: hh, tenure, home_type: homeType || null });
      onCreated(data);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" noValidate data-testid="create-hub-form">
      <Field label="Family name" htmlFor="hub-name" error={error} errorTestid="create-hub-error">
        <input id="hub-name" className="q-input" placeholder="e.g. The Rivera Family" value={name}
          aria-invalid={!!error} onChange={(e) => { setName(e.target.value); setError(""); }} data-testid="create-hub-name-input" />
      </Field>
      <Field label="Time zone" htmlFor="hub-tz">
        <TimezoneSelect id="hub-tz" value={tz} onChange={setTz} testid="create-hub-timezone-select" />
      </Field>
      <div className="flex gap-2" role="radiogroup" aria-label="Hub type" data-testid="create-hub-type">
        {[["family", "Family"], ["roommates", "Roommates"]].map(([v, l]) => <button type="button" key={v} role="radio" aria-checked={kind === v} className={`q-btn min-h-[44px] flex-1 ${kind === v ? "q-btn-primary" : "q-btn-secondary"}`} onClick={() => setKind(v)} data-testid={`create-hub-type-${v}`}>{l}</button>)}
      </div>
      {kind === "roommates" && <p className="text-xs text-q-muted" data-testid="create-hub-roommates-note">Everyone is an equal roommate. Shared bills and chores are shared; each person's own money and documents stay private to them.</p>}
      {kind === "family" && <fieldset data-testid="create-hub-household">
        <legend className="q-label mb-2">Who's in your home?</legend>
        {[["kids", "Kids"], ["pets", "Pets"], ["cars", "Cars"]].map(([k, l]) => (
          <div key={k} className="mb-2 flex items-center justify-between gap-3">
            <span className="text-sm text-q-text">{l}</span>
            <div className="flex gap-1">
              {[[true, "Yes"], [false, "No"]].map(([v, t]) => (
                <button type="button" key={t} className={`q-btn min-h-[44px] min-w-[64px] ${hh[k] === v ? "q-btn-primary" : "q-btn-secondary"}`} aria-pressed={hh[k] === v} onClick={() => setHh({ ...hh, [k]: v })} data-testid={`create-hub-${k}-${t.toLowerCase()}`}>{t}</button>
              ))}
            </div>
          </div>
        ))}
        <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
          <span className="text-sm text-q-text">Do you rent or own?</span>
          <div className="flex gap-1">
            {[["rent", "Rent"], ["own", "Own"]].map(([v, t]) => (
              <button type="button" key={v} className={`q-btn min-h-[44px] min-w-[64px] ${tenure === v ? "q-btn-primary" : "q-btn-secondary"}`} aria-pressed={tenure === v} onClick={() => setTenure(v)} data-testid={`create-hub-tenure-${v}`}>{t}</button>
            ))}
          </div>
        </div>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
          <label htmlFor="create-hub-home-type" className="text-sm text-q-text">Home type</label>
          <select id="create-hub-home-type" className="q-input min-h-[44px] w-auto" value={homeType} onChange={(e) => setHomeType(e.target.value)} data-testid="create-hub-home-type">
            <option value="">Choose</option>{HOME_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <p className="text-xs text-q-muted">No kids hides Kids Corner. Rent turns on Lease, Move-in/out log and Maintenance; Own turns on Housing costs. You can change this later in Settings.</p>
      </fieldset>}
      <button className="q-btn q-btn-primary w-full" disabled={busy} data-testid="create-hub-submit">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create hub
      </button>
    </form>
  );
}
